import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "sonner";
import React, { Suspense, lazy } from "react";
import AppLayout from "./components/AppLayout";

// Lazy load all pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const WalletsPage = lazy(() => import("./pages/WalletsPage"));
const ExchangePage = lazy(() => import("./pages/ExchangePage"));
const TransactionsPage = lazy(() => import("./pages/TransactionsPage"));
const AdminPageModule = lazy(() => import("./pages/AdminPage"));
const SendPage = lazy(() => import("./pages/SendPage"));
const ReceivePage = lazy(() => import("./pages/ReceivePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const VerificationPage = lazy(() => import("./pages/VerificationPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const LegalHubPage = lazy(() => import("./pages/LegalHubPage"));
const LegalDocPage = lazy(() => import("./pages/LegalDocPage"));
const PrivacyPortalPage = lazy(() => import("./pages/PrivacyPortalPage"));
const BuildingTrustPage = lazy(() => import("./pages/BuildingTrustPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));

// Lazy wrappers for admin sub-routes
const AdminDashboard = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminUsers })));
const AdminWallets = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminWallets })));
const AdminTransactions = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminTransactions })));
const AdminNotifications = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminNotifications })));
const AdminPassword = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminPassword })));
const AdminStatus = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminStatus })));

const PageSpinner = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError(error) {
    const msg = String(error?.message || '');
    // Chrome/Yandex auto-translate corrupts DOM → React reconciler crashes with these.
    // Just self-recover: re-render the tree (translated DOM is harmless after).
    if (msg.includes('removeChild') || msg.includes('insertBefore') || msg.includes('Failed to execute')) {
      console.warn('Auto-translate DOM mismatch — recovering silently');
      return { hasError: false };
    }
    return { hasError: true };
  }
  componentDidCatch(error) { console.warn('ErrorBoundary caught:', error?.message); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
          <div className="text-center space-y-4 p-8">
            <p className="text-lg font-semibold">Произошла ошибка</p>
            <p className="text-xs text-muted-foreground max-w-xs">Если это повторяется — проверьте, не включен ли в браузере автоперевод страницы (он ломает приложение).</p>
            <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-6 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary transition-colors text-sm">
              Перезагрузить
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (user) {
    if (!user.is_admin && user.verification_status === 'unverified') return <Navigate to="/verification" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (!user?.is_admin && user?.verification_status === 'unverified') return <Navigate to="/verification" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/legal" element={<LegalHubPage />} />
        <Route path="/legal/:slug" element={<LegalDocPage />} />
        <Route path="/privacy-portal" element={<PrivacyPortalPage />} />
        <Route path="/building-trust" element={<BuildingTrustPage />} />
        <Route path="/products/:slug" element={<ProductPage />} />
        <Route path="/support/:slug" element={<SupportPage />} />
        <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route index element={<DefaultRedirect />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="wallets" element={<WalletsPage />} />
          <Route path="send" element={<SendPage />} />
          <Route path="receive" element={<ReceivePage />} />
          <Route path="exchange" element={<ExchangePage />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="verification" element={<VerificationPage />} />
          <Route path="admin" element={<AdminPageModule />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="wallets" element={<AdminWallets />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="password" element={<AdminPassword />} />
            <Route path="status" element={<AdminStatus />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="noise-overlay">
              <AppRoutes />
              <Toaster position="top-right" richColors closeButton />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
